import React, { useEffect } from "react";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import { ReduxStoreState } from "web/redux/store";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import { closeProductCardModal, ProductCardModal } from "web/redux/ducks/send-to-app-modal";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import history from "web/script/utils/history";
import { CampaignRawBranchLink } from "web/script/utils/branch-io";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";

type FeedType = "general" | "is_pdp_related";

interface ProductCardSendToAppModalProps {
    paywalledClassName: string;
    feedType: FeedType;
}

function extractState(state: ReduxStoreState): ProductCardModal {
    return state.sendToAppModalReducer.productCard;
}

const APP_MODAL_NAME = "product-card-block-overlay";
const ANALYTICS_EVENT_LABEL = "product_card_block";
// IS PDP 'Similar Products' related section
const ANALYTICS_EVENT_LABEL_IS_RELATED = "product_card_block_is_related";
// IS PDP 'Recently OOS' related section
const ANALYTICS_EVENT_LABEL_OOS_RELATED = "product_card_block_oos_related";

const TITLE_KEYS = {
    title_1: gettextNoop("product_card.app_overlay_block.title_1"),
    title_2: gettextNoop("product_card.app_overlay_block.title_2"),
    title_3: gettextNoop("product_card.app_overlay_block.title_3"),
    title_4: gettextNoop("product_card.app_overlay_block.title_4"),
};

const IS_PDP_RELATED_TITLE_KEYS = {
    title_1: gettextNoop("is_pdp_related.product_card.app_overlay_block.title_1"),
    title_2: gettextNoop("is_pdp_related.product_card.app_overlay_block.title_2"),
    title_3: gettextNoop("is_pdp_related.product_card.app_overlay_block.title_3"),
    title_4: gettextNoop("is_pdp_related.product_card.app_overlay_block.title_4"),
};

export default function ProductCardSendToAppModal({
    paywalledClassName,
    feedType,
}: ProductCardSendToAppModalProps): JSX.Element {
    const dispatch = useDispatch();
    const { openModal, productUrl, productImageUrl, inStock } = useReduxSelector(extractState);

    const keepModalOpen = !!useOverlayWatcher(APP_MODAL_NAME);
    useEffect(() => {
        if (!keepModalOpen) {
            dispatch(closeProductCardModal());
            history.popOverlay(APP_MODAL_NAME);
        }
    }, [keepModalOpen, dispatch]);

    useEffect(() => {
        if (openModal) {
            // I check the current overlay state instead of modalIsOpen
            // to get around the delay introduced by hooks
            if (history.getCurrentState().overlay != APP_MODAL_NAME) {
                history.pushOverlay(APP_MODAL_NAME);
            }
        }
    }, [openModal]);

    let fullProductUrl;
    if (productUrl) {
        try {
            fullProductUrl = new URL(productUrl, globals.window.location.href);
        } catch (err) {
            console.error(`${productUrl} is not a valid URL`);
        }
    }

    let titleVariant;
    let blockVariant;
    let branchLink;
    let title = "";
    let continueOption;
    let analyticsEventLabel;
    if (feedType === "general") {
        analyticsEventLabel = ANALYTICS_EVENT_LABEL;
        titleVariant = environment.getFeature("mg_block_product_card_title");
        blockVariant = environment.getFeature("mg_block_product_card");
        branchLink = CampaignRawBranchLink.ProductCardGeneralFeed;
        title = gettext(TITLE_KEYS[titleVariant]);
    } else if (feedType === "is_pdp_related") {
        analyticsEventLabel = inStock
            ? ANALYTICS_EVENT_LABEL_IS_RELATED
            : ANALYTICS_EVENT_LABEL_OOS_RELATED;
        titleVariant = environment.getFeature("mg_block_is_pdp_related_product_card_title");
        blockVariant = environment.getFeature("mg_block_is_pdp_related_product_card");
        branchLink = CampaignRawBranchLink.ProductCardISPDPRelatedFeed;
        title = gettext(IS_PDP_RELATED_TITLE_KEYS[titleVariant]);

        // testing a different soft block experience - 'continue shopping' will take the user to the product
        if (blockVariant === "soft_block") {
            continueOption = (
                <ContinueToOption
                    href={fullProductUrl}
                    continueMessage={gettext("general.continue_shopping_btn_text")}
                    analyticsEventLabel={analyticsEventLabel}
                />
            );
        }
    }

    function onClose(): void {
        dispatch(closeProductCardModal());

        // if we've given the user the option to continue then don't go to the product on close
        if (blockVariant === "soft_block" && !continueOption) {
            globals.window.location.href = fullProductUrl.href;
        }
    }

    return (
        <SendToAppModal
            isOpen={openModal}
            onClose={onClose}
            title={title}
            url={branchLink}
            analyticsEventLabel={analyticsEventLabel}
            paywalledClassName={paywalledClassName}
            appDeepLinkURL={fullProductUrl}
            productImageURL={productImageUrl}
            extraContent={(): JSX.Element => {
                return continueOption;
            }}
        />
    );
}
